import { MapPin, Users, CurrencyEur } from '@phosphor-icons/react';
import { Card, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { FlowIndicator } from './FlowIndicator';
import { CandidateUnit } from '../hooks/useHerverhuurPipeline';

interface CandidatesCardProps {
  unit: CandidateUnit;
}

const CANDIDATE_STATUS_LABEL: Record<string, string> = {
  'awaiting-visit': 'Bezoek gepland',
  'awaiting-interested': 'Na bezoek',
  'interested': 'Dossier ingediend',
  'approved': 'Goedgekeurd',
  'awaiting-alternate-selection': 'Alt. datum',
  searchingAppointmentMoment: 'Afspraak zoeken',
  'forwarded-to-broker': 'Via makelaar',
};

export const CandidatesCard = ({ unit }: CandidatesCardProps) => {
  const visibleCandidates = unit.candidates.slice(0, 2);
  const extraCount = unit.candidates.length - visibleCandidates.length;

  return (
    <Card className="border border-border shadow-sm hover:shadow-md transition-shadow cursor-default">
      <CardContent className="p-3 flex flex-col gap-2">
        <div className="flex items-start gap-1.5 min-w-0">
          <MapPin className="text-neutral-secondary-mid mt-0.5 flex-shrink-0" size={13} weight="fill" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-neutral-primary leading-tight truncate">
              {unit.buildingName || unit.buildingId}
            </p>
            <p className="text-[11px] text-neutral-secondary-mid">
              {unit.unitLabel} • {unit.floor !== null ? `Verd. ${unit.floor}` : '—'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <Users className="text-blue-500 flex-shrink-0" size={13} />
          <p className="text-xs text-neutral-primary font-medium">
            {unit.candidates.length} kandidaat{unit.candidates.length !== 1 ? 'en' : ''}
          </p>
        </div>

        <div className="flex flex-col gap-1">
          {visibleCandidates.map((c) => (
            <div key={c.applicationId} className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-neutral-primary truncate">
                {c.firstName} {c.lastName}
              </span>
              <Badge
                variant="outline"
                className="flex-shrink-0 text-[10px] h-4 px-1 border-blue-200 text-blue-600 bg-blue-50"
              >
                {CANDIDATE_STATUS_LABEL[c.status] ?? c.status}
              </Badge>
            </div>
          ))}
          {extraCount > 0 && (
            <p className="text-[11px] text-neutral-secondary-mid">
              + {extraCount} meer
            </p>
          )}
        </div>

        {unit.advertisedRentalPrice && (
          <div className="flex items-center gap-1.5 pt-1 border-t border-border">
            <CurrencyEur className="text-neutral-secondary-mid flex-shrink-0" size={13} />
            <p className="text-xs text-neutral-secondary-mid">
              <span className="text-neutral-primary font-medium">€{unit.advertisedRentalPrice}/mnd</span>
            </p>
          </div>
        )}

        <div className="flex items-center pt-1 border-t border-border">
          <FlowIndicator activeStep={4} />
        </div>
      </CardContent>
    </Card>
  );
};
